import React, { ChangeEvent, useContext, useEffect, useState } from "react"
import { XmppContext } from "../stream/xmppClient"
import AdHocCommands from "../api/adHocCommands"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

type WhatsappAccountStatusDialogProps = {
  isOpen: boolean
  status: string | undefined
  onClose: () => void
  onSaveSuccess: (newStatus: string) => void
}

const WhatsappAccountStatusDialog = ({
  isOpen,
  status,
  onClose,
  onSaveSuccess,
}: WhatsappAccountStatusDialogProps) => {
  const { t } = useTranslation()
  const [newStatus, setNewStatus] = useState<string | undefined>("")
  const { client } = useContext(XmppContext)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // Update newStatus when dialog opens or status changes
  useEffect(() => {
    if (isOpen) {
      setNewStatus(status || "")
    }
  }, [isOpen, status])

  const onStatusChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNewStatus(event.target.value)
  }

  const onSave = async () => {
    setIsSaving(true)
    // to remove status from WhatsApp we sent <space> because empty step does not remove it
    const finalStatus = newStatus && newStatus.length > 0 ? newStatus : " "
    await AdHocCommands.updateWhatsappStatus({ client, status: finalStatus })
    setIsSaving(false)
    onSaveSuccess(newStatus || "")
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="gap-1 sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("fleet.whatsappStatus.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="mb-3 mt-0 flex flex-col gap-2 space-y-2">
          <Label className="mb-2 text-sm text-muted-foreground">
            {t("fleet.whatsappStatus.description")}
          </Label>
          <Textarea
            placeholder={t("fleet.whatsappStatus.placeholder")}
            value={newStatus || ""}
            onChange={onStatusChange}
            className="min-h-[100px] resize-none"
            disabled={isSaving}
          />
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("fleet.form.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default WhatsappAccountStatusDialog
